
/**
 * Module dependencies.
 */

import { ProjectProps } from 'src/types/user';
import { assetUrlResolve } from 'src/core/utils/url-resolver';
import { color, units } from 'src/styles/utils';
import { myRevigresRoutes } from 'src/core/routes';
import { routeTemplate } from 'src/core/utils/routes';
import { theme } from 'styled-tools';
import { useDateFormatter } from 'src/core/utils/formatter';
import { useTranslate } from 'src/core/utils/translator';
import CardTitle from 'src/components/core/typography/card-title';
import Image, { ImageProps } from 'src/components/core/image';
import Label from 'src/components/core/typography/label';
import React, { ReactElement } from 'react';
import RouterLink from 'src/components/core/links/router-link';
import get from 'lodash/get';
import head from 'lodash/head';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = Pick<ProjectProps, 'createdAt' | 'id' | 'media' | 'title'>;

/**
 * `StyledImage` styled component.
 */

const StyledImage = styled(Image)<ImageProps>`
  transition: transform 5s ease-in;
`;

/**
 * `Card` styled component.
 */

const Card = styled(RouterLink)`
  background-color: ${color('grey100')};
  color: ${color('textColor')};
  display: grid;
  grid-template-areas:
    'image image image'
    '.     info  .';
  grid-template-columns: ${units(3)} 1fr ${units(3)};
  grid-template-rows: ${units(36)} ${units(16)};
  transition: color ${theme('animations.defaultTransition')};

  &:focus,
  &:hover {
    color: ${color('brown400')};

    ${StyledImage} {
      transform: scale(1.5);
    }
  }
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  grid-area: image;
  height: ${units(36)};
  overflow: hidden;
  position: relative;
`;

/**
 * `InfoWrapper` styled component.
 */

const InfoWrapper = styled.div`
  grid-area: info;
  height: ${units(16)};
`;

/**
 * `CreatedAtLabel` styled component.
 */

const CreatedAtLabel = styled(Label).attrs({ size: 'small' })`
  color: ${color('grey300')};
  grid-area: createdAt;
  letter-spacing: 0;
  padding-top: ${units(3)};
  text-transform: uppercase;
`;

/**
 * `Title` styled component.
 */

const Title = styled(CardTitle).attrs({ size: 'small' })`
  grid-area: title;
`;

/**
 * `MyRevigresProjectCard` component.
 */

const MyRevigresProjectCard = (props: Props): ReactElement => {
  const { locale } = useTranslate();
  const dateFormatter = useDateFormatter();
  const { createdAt, id, media, title } = props;
  const imageUrl = get(head(media), 'fileUrl');

  return (
    <Card
      href={routeTemplate(myRevigresRoutes.project, { id })}
      locale={locale}
    >
      <ImageWrapper>
        <StyledImage
          alt={title}
          layout={'fill'}
          objectFit={'cover'}
          src={imageUrl ?? assetUrlResolve('/static/images/empty/list@2x.jpg')}
        />
      </ImageWrapper>

      <InfoWrapper>
        <CreatedAtLabel>
          {dateFormatter.format(new Date(createdAt))}
        </CreatedAtLabel>

        <Title>
          {title}
        </Title>
      </InfoWrapper>
    </Card>
  );
};

/**
 * Export `MyRevigresProjectCard` component.
 */

export default MyRevigresProjectCard;
